import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext } from 'react';
import styled, { ThemeContext } from 'styled-components';
import { breakpoints } from '@awaze/design-system.utils.react';
export const calculateColumnWidth = (columns, maxColumns) => (columns >= maxColumns ? 100 : (100 / maxColumns) * columns);
export const generateResponsiveStyles = (values, maxColumns, style) => {
    if (values.length > maxColumns.length) {
        throw new Error('you may not have more columns than maxColumns');
    }
    const slicedValues = values.slice(0, breakpoints.length + 1);
    return slicedValues.reduce((acc, value, index) => {
        const calculatedValue = calculateColumnWidth(value, maxColumns[index]);
        if (index === 0) {
            return `${acc}${style}: ${calculatedValue}%;\n`;
        }
        return `${acc}@media (min-width: ${breakpoints[index - 1]}px) {
  ${style}: ${calculatedValue}%;
}\n`;
    }, '');
};
export const generateResponsiveWidths = (columns, maxColumns) => generateResponsiveStyles(columns, maxColumns, 'width');
export const generateResponsiveOffsets = (offsets, maxColumns) => generateResponsiveStyles(offsets, maxColumns, 'margin-left');
const BaseGridItemStyled = styled.div `
  box-sizing: border-box;
  padding: 0 ${({ theme }) => theme.GridBaseGridSmall0GutterSize / 2}px;

  ${({ columns, maxColumns }) => Array.isArray(columns)
    ? `
    ${generateResponsiveWidths(columns, maxColumns)}
  `
    : `
    width: ${calculateColumnWidth(columns, maxColumns[0])}%;
  `}

  ${({ offsets, maxColumns }) => Array.isArray(offsets) &&
    `${generateResponsiveOffsets(offsets, maxColumns)}`}

  ${({ offsets, maxColumns }) => !Array.isArray(offsets) &&
    offsets !== 0 &&
    `
    margin-left: ${calculateColumnWidth(offsets, maxColumns[0])}%;
  `}

  @media (min-width: ${breakpoints[0]}px) {
    padding: 0 ${({ theme }) => theme.GridBaseGridMedium0GutterSize / 2}px;
  }

  @media (min-width: ${breakpoints[1]}px) {
    padding: 0 ${({ theme }) => theme.GridBaseGridLarge0GutterSize / 2}px;
  }
`;
/**
 The `<BaseGridItem>` is designed to be used as a child of the `<BaseGrid>` component.

 `<BaseGridItem>` takes a prop of `columns` to determine how many columns
 to spread the container across, out of 12.

 The `columns` prop can be a number or an array of numbers. A single number will
 set the column width for all screen sizes. An array of numbers will responsively
 change the column width based on the systems predetermined breakpoints starting
 from the smallest breakpoint all the way up to the largest.

 If you provide a column number that exceeds the max columns available for default
 or the breakpoint size then the `<BaseGridItem>` will render at 100% width.

 `<BaseGridItem>`s can span multiple rows by default so if the next column won't
 fit it will break to a new line.
 */
export const BaseGridItem = ({ children, columns = 12, offsets = 0, ...rest }) => {
    const themeContext = useContext(ThemeContext);
    const maxColumns = [
        themeContext?.GridBaseGridSmall0Count || 12,
        themeContext?.GridBaseGridMedium0Count || 12,
        themeContext?.GridBaseGridLarge0Count || 12,
    ];
    return (_jsx(_Fragment, { children: _jsx(BaseGridItemStyled, { columns: columns, maxColumns: maxColumns, offsets: offsets, ...rest, children: children }) }));
};
//# sourceMappingURL=base-grid-item.js.map